import axios from 'axios';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Badge, Button, Form, Tab, Nav, Modal } from 'react-bootstrap';
import { attachments } from 'data/kanban';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import UserMessagingShimmer from './UserMessagingShimmer';
import Flex from 'components/common/Flex';
import UserMessageSection from './UserMessageSection';

const UserMessagingView = ({ ticketData, loader }) => {
    const { id } = useParams();
    const [initialMessage, setInitialMessage] = useState(null);
    const [ticket, setTicket] = useState(ticketData);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [sending, setSending] = useState(false);
    const [navItems, setNavItems] = useState([]);
    const [activeTab, setActiveTab] = useState('Messaging');
    const [showDescModal, setShowDescModal] = useState(false);
    const isLongDescription =
        initialMessage?.comment &&
        initialMessage?.comment?.length > 100
    const currentUserRoleId = localStorage.getItem('roleId');
    const currentUserName = localStorage.getItem('user-name');
    const statusColor = {
        open: 'success',
        closed: 'secondary',
        pending: 'warning'
    };


    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        let results = ['Messaging'];
        if (Number(localStorage.getItem('roleId')) < 4) {
            results.push('Internal-Notes');
        }
        setNavItems(results)
    }, [])

    const fetchData = async () => {
        setTicket(ticketData);

        // 🔥 Create initial message from ticket description
        const initialMessage = {
            id: `ticket-${ticketData?.id}`,
            comment: ticketData?.description,
            created_by_name: ticketData?.created_by_name,
            created: ticketData?.created,
            page_url: ticketData?.page_url,
            attachments: ticketData?.attachments // Only include screenshots
        };
        setInitialMessage(initialMessage);
        // Combine initial message + comments
        setMessages(ticketData?.comments);

    };


    const handleSend = async () => {
        if (!newMessage.trim()) return;

        const tokenStr = localStorage.getItem('accessToken');
        setSending(true);
        const payload = { comment: newMessage };
        if (activeTab === 'Internal-Notes') {
            payload.is_internal = 1;
        }
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/support/${id}/comments`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${tokenStr}`
                    }
                }
            );
            const words = currentUserName.split(" ");

            const newComment = {
                id: data.insertId,
                comment: newMessage,
                last_name: words[1],
                first_name: words[0],
                role_id: currentUserRoleId,
                created: new Date().toISOString()
            };

            if (activeTab === 'Internal-Notes') {
                newComment.is_internal = 1;
            }

            setMessages(prev => [...prev, newComment]);
            setNewMessage('');

        } catch (error) {
            console.error(error);
        } finally {
            setSending(false);
        }
    };



    // if (!ticket) return <UserMessagingShimmer />;


    return (
        <Card style={{ height: 'calc(100vh - 130px)' }}>
            {/* Header */}
            <div className="bg-light border-bottom p-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap">

                    <div className="me-3 flex-grow-1">
                        <h5 className="mb-1 text-truncate">{ticket?.subject}</h5>

                        <div className="text-muted small mb-2">
                            {ticket?.ticket_number}
                        </div>
                    </div>

                    <div className="d-flex gap-2 mb-2 me-2">
                        <Badge bg={statusColor[ticket?.status]} className="text-capitalize">
                            {ticket?.status}
                        </Badge>
                        <Badge bg="warning" className="text-capitalize">
                            {ticket?.priority}
                        </Badge>
                    </div>
                    {initialMessage?.attachments?.length > 0 && (
                        <div className="d-flex flex-wrap gap-2">
                            {initialMessage.attachments
                                .filter(file => {
                                    // If roleId > 3 → hide screenshots
                                    if (Number(currentUserRoleId) > 3) {
                                        return file.file_type !== 'screenshot';
                                    }
                                    return true; // else show all
                                })
                                .map((file, index) => (
                                    <a
                                        key={file.id}
                                        href={file.download_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="badge bg-light text-dark text-decoration-none border"
                                    >
                                        {file.file_type === 'screenshot'
                                            ? '📷 Screenshot'
                                            : '📎 Attachment'}
                                    </a>
                                ))}
                        </div>
                    )}
                </div>

                {initialMessage?.comment && (
                    <div>
                        <p
                            className="mb-1 text-muted"
                            style={{
                                maxHeight: '30px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal'
                            }}
                        >
                            {initialMessage.comment}
                        </p>
                        {isLongDescription && (
                            <span
                                className="text-primary medium"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowDescModal(true)}
                            >
                                Read more
                            </span>
                        )}
                    </div>
                )}

                {initialMessage?.page_url && (
                    <div>
                        <a
                            href={initialMessage.page_url}
                            target="_blank"
                            rel="noreferrer"
                            className="small text-primary"
                        >
                            🔗 {initialMessage.page_url}
                        </a>
                    </div>
                )}


            </div>

            {/* Chat Scrollable Area */}
            <Tab.Container
                id="audience-tab"
                defaultActiveKey="Messaging"
                className="d-flex flex-column h-100"
                onSelect={(k) => setActiveTab(k)}
            >
                <Card.Header as={Flex}
                    justifyContent="between"
                    alignItems="center"
                    className="pb-3 border-bottom patientInfoTabs">
                    <Nav variant="tabs" className="w-100">
                        {navItems.map(item => (
                            <Nav.Item key={item}>
                                <Nav.Link eventKey={item}>
                                    {item.replace('-', ' ').toUpperCase()}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>

                </Card.Header>
                <Card.Body
                    id="audience-tab-tabpane-Messaging"
                >
                    <Tab.Content>
                        <Tab.Pane unmountOnExit eventKey="Messaging">
                            <UserMessageSection
                                messages={messages}
                                newMessage={newMessage}
                                setNewMessage={setNewMessage}
                                handleSend={handleSend}
                                sending={sending}
                                eventKey="Messaging"
                            />
                        </Tab.Pane>
                        <Tab.Pane unmountOnExit eventKey="Internal-Notes">
                            <UserMessageSection
                                messages={messages}
                                newMessage={newMessage}
                                setNewMessage={setNewMessage}
                                handleSend={handleSend}
                                sending={sending}
                                eventKey="Internal-Notes" />
                        </Tab.Pane>
                    </Tab.Content>
                </Card.Body>
                <Card.Footer className="p-3">
                    <div className="d-flex gap-2">
                        {/* Input Fixed Bottom */}
                        <Form.Control
                            type="text"
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !sending) {
                                    handleSend();
                                }
                            }}
                        />
                        <Button onClick={handleSend} disabled={sending}>
                            {sending ? 'Sending...' : 'Send'}
                        </Button>
                    </div>
                </Card.Footer>
            </Tab.Container>

            <Modal
                show={showDescModal}
                onHide={() => setShowDescModal(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Ticket Description</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>
                    {initialMessage?.comment}
                </Modal.Body>
            </Modal>

        </Card>
    );

};

export default UserMessagingView;